import { Box, Grid, Typography } from '@mui/material';
import { ProfileForm } from '@/components/forms/ProfileForm';
import { MainCard } from '@/components/molecules/MainCard';
import { Loading } from '@/components/atoms/Loading';
import type { AuthUser, Partner } from '@/openapi';
import { useUpdatePartnerMutation } from '@/hooks/useUpdatePartnerMutation';
import { useSnackbar } from 'notistack';
import { PartnersService, UsersService } from '@/openapi';
import { useAuth } from '@/providers/AuthProvider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/util/queryKeys';

export const Profile: React.FC = () => {
  const { authUser, profile } = useAuth();
  const snackbar = useSnackbar();

  const { isLoading, data: partner } = useQuery({
    queryKey: queryKeys.profile.data(),
    queryFn: () =>
      PartnersService.partnersServiceGetPartner({
        path: {
          id: profile?.id ?? 22,
        },
      }),
    enabled: profile !== null,
  });

  const partnerMutatation = useUpdatePartnerMutation();
  const userMutation = useMutation((authUser: AuthUser) => {
    return UsersService.usersServiceUpdateUser({
      path: {
        id: authUser.uid,
      },
      body: authUser,
    });
  });

  const updateProfileAndUser = async (partner: Partner) => {
    try {
      if (!partner || !authUser) {
        throw Error('Partner or user object not found');
      }

      await Promise.all([
        partnerMutatation.mutateAsync(partner),
        userMutation.mutateAsync({
          uid: authUser.uid,
          displayName: `${partner.firstName} ${partner.lastName}`,
          email: partner.email,
          role: authUser.role,
        }),
      ]);

      snackbar.enqueueSnackbar('Profile updated successfully', {
        variant: 'success',
      });
    } catch {
      snackbar.enqueueSnackbar('Profile failed to update', {
        variant: 'error',
      });
    }
  };

  return (
    <Grid
      size={{
        xs: 12,
        lg: 6,
      }}>
      <Typography variant="h1">Profile</Typography>
      <Box
        sx={{
          m: 4,
        }}
      />
      <MainCard>
        {partner && authUser ? (
          <ProfileForm
            partner={partner.data}
            partnerUser={authUser}
            onSubmit={updateProfileAndUser}
          />
        ) : isLoading ? (
          <Loading />
        ) : (
          'No profile is associated with this login.'
        )}
      </MainCard>
      <Box
        sx={{
          m: 4,
        }}
      />
    </Grid>
  );
};
